import React, { useState, useEffect, useRef, useMemo } from 'react';
import { parseMarkdown } from '../utils/markdownUtils';

const TypewriterMarkdown = ({ 
  text, 
  speed = 8,  // Reduced from 18 for faster typing
  minSpeed = 4,  // Reduced from 8
  maxSpeed = 15,  // Reduced from 25
  onComplete = null,
  startDelay = 50,  // Reduced from 100
  className = ''
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const timeoutRef = useRef(null);
  const onCompleteRef = useRef(onComplete);

  // Cache the parsed markdown structure to avoid re-parsing on every render
  const parsedElements = useMemo(() => {
    if (!text) return [];
    return parseMarkdown(text);
  }, [text]);

  // Update the ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Reset state when text changes
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!text) {
      return;
    }

    // If speed is 0, show all text immediately
    if (speed === 0) {
      setDisplayedText(text);
      setIsComplete(true);
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
      return;
    }

    // Start typing after delay
    timeoutRef.current = setTimeout(() => {
      setCurrentIndex(1);
    }, startDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, startDelay, speed]);

  useEffect(() => {
    if (!text || currentIndex === 0 || isComplete) return;

    if (currentIndex <= text.length) {
      // Variable speed based on character type
      const currentChar = text[currentIndex - 1];
      let charSpeed = speed;

      if (currentChar === ' ') {
        charSpeed = maxSpeed;
      } else if (['.', '!', '?', ',', ';', ':'].includes(currentChar)) {
        charSpeed = minSpeed;
      }

      timeoutRef.current = setTimeout(() => {
        setDisplayedText(text.substring(0, currentIndex));
        setCurrentIndex(prev => prev + 1);
      }, charSpeed);
    } else if (!isComplete) {
      // Animation complete
      setIsComplete(true);
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentIndex, text, speed, minSpeed, maxSpeed, isComplete]);

  // Render the currently displayed text with markdown formatting using cached elements
  const renderFormattedText = (textToRender) => {
    if (!textToRender || !parsedElements.length) return null;
    
    // For performance: if text is incomplete (typewriter effect), parse only displayed portion
    // If text is complete or instant (speed === 0), use cached full elements
    const elements = textToRender === text ? parsedElements : parseMarkdown(textToRender);
    
    return elements.map((element, index) => {
      switch (element.type) {
        case 'header':
          const HeaderTag = `h${Math.min(element.level, 6)}`;
          return (
            <div 
              key={index} 
              className={`markdown-header markdown-h${element.level}`}
              style={{ marginLeft: `${element.indent * 8}px` }}
            >
              <HeaderTag>{renderInlineElements(element.elements)}</HeaderTag>
            </div>
          );
        
        case 'bullet':
          // Use proper bullet symbols based on nesting level
          const bulletSymbols = ['•', '◦', '▪', '▫'];
          const bulletSymbol = bulletSymbols[element.nestLevel % bulletSymbols.length];
          
          return (
            <div 
              key={index} 
              className="markdown-bullet"
              style={{ 
                marginLeft: `${element.nestLevel * 24}px`,
                paddingLeft: '16px',
                textIndent: '-16px'
              }}
            >
              <span className="bullet-symbol">{bulletSymbol}</span>
              <span className="bullet-content"> {renderInlineElements(element.elements)}</span>
            </div>
          );
        
        case 'numbered':
          return (
            <div 
              key={index} 
              className="markdown-numbered"
              style={{ 
                marginLeft: `${element.nestLevel * 24}px`,
                paddingLeft: '20px',
                textIndent: '-20px'
              }}
            >
              <span className="number-symbol">{element.number}.</span>
              <span className="number-content"> {renderInlineElements(element.elements)}</span>
            </div>
          );
        
        case 'code':
          return (
            <pre 
              key={index} 
              className="markdown-code-block"
              style={{ marginLeft: `${element.indent * 8}px` }}
            >
              <code>{element.content}</code>
            </pre>
          );
        
        case 'blockquote':
          return (
            <blockquote 
              key={index} 
              className="markdown-blockquote"
              style={{ 
                marginLeft: `${element.indent * 8}px`,
                paddingLeft: `${element.level * 16}px`,
                borderLeft: `${element.level * 2}px solid #ddd`
              }}
            >
              {renderInlineElements(element.elements)}
            </blockquote>
          );
        
        case 'break':
          return <br key={index} />;
        
        case 'paragraph':
        default:
          return (
            <div 
              key={index} 
              className="markdown-paragraph"
              style={{ marginLeft: `${element.indent * 8}px` }}
            >
              {renderInlineElements(element.elements)}
            </div>
          );
      }
    });
  };

  const renderInlineElements = useMemo(() => (elements, prefix = '') => {
    return (
      <>
        {prefix}
        {elements.map((element, index) => {
          switch (element.type) {
            case 'bold':
              return <strong key={index}>{element.content}</strong>;
            
            case 'italic':
              return <em key={index}>{element.content}</em>;
            
            case 'code':
              return (
                <code key={index} className="markdown-inline-code">
                  {element.content}
                </code>
              );
            
            case 'link':
              return (
                <a 
                  key={index} 
                  href={element.url}
                  className="markdown-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {element.content}
                </a>
              );
            
            case 'text':
            default:
              return <span key={index}>{element.content}</span>;
          }
        })}
      </>
    );
  }, []);

  return (
    <div className={`typewriter-markdown ${className}`}>
      {renderFormattedText(displayedText)}
    </div>
  );
};

export default React.memo(TypewriterMarkdown);